import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  UploadCloud, Sparkles, Loader2, ArrowLeft, CheckCircle2, FileText, Wand2, GitBranch, AlertTriangle, ChevronDown, Camera, X, ScanLine,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TIPI_DOCUMENTO, STATI_DOCUMENTO } from "@/lib/constants";
import { toast } from "sonner";

const emptyForm = {
  codice: "", titolo: "", tipo: "PG", descrizione: "", versione: "1.0",
  stato: "vigente", data_emissione: "", data_prossima_revisione: "",
  scheme_ids: [], process_ids: [], requirement_ids: [], linked_document_ids: [],
};

export default function DocumentUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const { tenantId, organizations } = useTenant();
  const [localTenant, setLocalTenant] = useState(tenantId || "");
  const [step, setStep] = useState(editId ? "form" : "upload");
  const [analyzing, setAnalyzing] = useState(false);
  const [file, setFile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [fileRef, setFileRef] = useState(null);
  const [raw, setRaw] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);

  const [schemes, setSchemes] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [docs, setDocs] = useState([]);

  const activeTenant = localTenant || tenantId;

  useEffect(() => { api.get("/schemes").then((r) => setSchemes(r.data)); }, []);
  useEffect(() => {
    if (!activeTenant) return;
    api.get(`/processes?tenant_id=${activeTenant}`).then((r) => setProcesses(r.data));
    api.get(`/documents?tenant_id=${activeTenant}`).then((r) => setDocs(r.data));
  }, [activeTenant]);
  useEffect(() => {
    const ids = form.scheme_ids || [];
    if (!ids.length) { setRequirements([]); return; }
    Promise.all(ids.map((sid) =>
      api.get(`/requirements?scheme_id=${sid}`)
        .then((r) => r.data.map((x) => ({ ...x, sigla: schemes.find((s) => s.id === sid)?.sigla || "" })))
        .catch(() => [])
    )).then((lists) => setRequirements(lists.flat()));
  }, [form.scheme_ids, schemes]);

  useEffect(() => {
    if (editId) {
      api.get(`/documents/${editId}`).then((r) => {
        const d = r.data;
        setLocalTenant(d.tenant_id);
        setForm({
          codice: d.codice || "", titolo: d.titolo || "", tipo: d.tipo || "PG",
          descrizione: d.descrizione || "", versione: d.versione || "1.0", stato: d.stato || "vigente",
          data_emissione: d.data_emissione || "", data_prossima_revisione: d.data_prossima_revisione || "",
          scheme_ids: d.scheme_ids?.length ? d.scheme_ids : (d.scheme_id ? [d.scheme_id] : []), process_ids: d.process_ids || [],
          requirement_ids: d.requirement_ids || [], linked_document_ids: d.linked_document_ids || [],
        });
        setFileRef(d.file || null);
      });
      api.get(`/documents/${editId}/impact`).then((r) => setImpact(r.data)).catch(() => {});
    }
  }, [editId]);

  const analyze = async () => {
    if (!activeTenant) { toast.error("Seleziona prima un cliente"); return; }
    if (photos.length > 0) return analyzePhotos();
    if (!file) { toast.error("Seleziona un file"); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("tenant_id", activeTenant);
      fd.append("file", file);
      const r = await api.post("/documents/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data.suggestions;
      setFileRef(r.data.file);
      setRaw(s.raw);
      setForm((f) => ({
        ...f,
        descrizione: s.descrizione || "",
        codice: s.codice_documento || "",
        titolo: s.titolo || file.name.replace(/\.[^.]+$/, ""),
        tipo: TIPI_DOCUMENTO.includes(s.tipo) ? s.tipo : "PG",
        versione: s.versione || "1.0",
        scheme_ids: s.suggested_scheme_id ? [s.suggested_scheme_id] : [],
        process_ids: s.suggested_process_ids || [],
        requirement_ids: s.suggested_requirement_ids || [],
      }));
      if (!r.data.text_found) toast.warning("Testo non estraibile dal file: compila i campi manualmente");
      else toast.success("Metadati proposti dall'AI");
      setStep("form");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAnalyzing(false); }
  };

  const analyzePhotos = async () => {
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("tenant_id", activeTenant);
      photos.forEach((p) => fd.append("files", p));
      const r = await api.post("/documents/extract-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data.suggestions;
      setFileRef(r.data.file);
      setRaw(s.raw);
      setForm((f) => ({
        ...f,
        descrizione: s.descrizione || "",
        codice: s.codice_documento || "",
        titolo: s.titolo || "Documento fotografato",
        tipo: TIPI_DOCUMENTO.includes(s.tipo) ? s.tipo : "PG",
        versione: s.versione || "1.0",
        scheme_ids: s.suggested_scheme_id ? [s.suggested_scheme_id] : [],
        process_ids: s.suggested_process_ids || [],
        requirement_ids: s.suggested_requirement_ids || [],
      }));
      if (!r.data.text_found) toast.warning("Testo non leggibile dalle foto: compila i campi manualmente");
      else toast.success(`${r.data.pages} pagine unite in un unico PDF`);
      setStep("form");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAnalyzing(false); }
  };

  const addPhotos = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    setFile(null);
    setScanned(false);
    setPhotos((p) => [...p, ...incoming].slice(0, 20));
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const enhanceScan = async () => {
    if (!activeTenant) { toast.error("Seleziona prima un cliente"); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("tenant_id", activeTenant);
      photos.forEach((p) => fd.append("files", p));
      const r = await api.post("/documents/scan-enhance", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = await Promise.all(
        r.data.pages.map(async (p) => {
          const blob = await (await fetch(p.data_url)).blob();
          return new File([blob], p.filename, { type: "image/png" });
        })
      );
      setPhotos(next);
      setScanned(true);
      toast.success("Pagine raddrizzate e ritagliate");
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanning(false); }
  };

  const toggle = (key, id) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
  }));

  const save = async () => {
    if (!form.codice || !form.titolo) { toast.error("Codice e titolo sono obbligatori"); return; }
    setSaving(true);
    try {
      const payload = { ...form, tenant_id: activeTenant, file: fileRef, scheme_ids: form.scheme_ids || [], scheme_id: (form.scheme_ids && form.scheme_ids[0]) || null };
      const r = editId ? await api.put(`/documents/${editId}`, payload) : await api.post("/documents", payload);
      toast.success("Documento salvato");
      navigate(`/documenti/${editId || r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
      </button>
      <div>
        <span className="label-caps">Schedatura assistita</span>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
          {editId ? "Modifica documento" : "Carica e scheda documento"}
        </h1>
      </div>

      {!editId && (
        <div className="flex items-center gap-2 text-sm">
          <StepDot active={step === "upload"} done={step === "form"} n={1} label="Caricamento & AI" />
          <div className="h-px w-8 bg-slate-200" />
          <StepDot active={step === "form"} n={2} label="Revisione & salvataggio" />
        </div>
      )}

      {step === "upload" && (
        <Card className="p-8 border-slate-200 space-y-6">
          <div>
            <Label>Cliente</Label>
            <Select value={activeTenant || ""} onValueChange={setLocalTenant}>
              <SelectTrigger className="mt-1.5 w-full md:w-80" data-testid="upload-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
              <SelectContent>
                {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <label className="block cursor-pointer" data-testid="file-dropzone">
            <input type="file" accept=".pdf,.docx,.txt,image/*" className="hidden"
              onChange={(e) => { setPhotos([]); setFile(e.target.files?.[0] || null); }} data-testid="file-input" />
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 sm:p-10 text-center hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
              <UploadCloud className="h-10 w-10 text-slate-500 mx-auto mb-3" />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm font-medium"><FileText className="h-4 w-4 text-blue-700" />{file.name}</div>
              ) : (
                <>
                  <p className="font-medium">Trascina o seleziona un file</p>
                  <p className="text-sm text-slate-500 mt-1">PDF, DOCX, TXT o una foto — l'AI estrarrà i metadati dall'intestazione</p>
                </>
              )}
            </div>
          </label>

          <label className="flex items-center justify-center gap-2.5 h-12 rounded-xl border border-indigo-400/40 bg-indigo-500/10 text-indigo-200 cursor-pointer hover:bg-indigo-500/20 hover:text-white transition-colors"
            data-testid="camera-dropzone">
            <input type="file" accept="image/*" capture="environment" multiple className="hidden"
              onChange={(e) => addPhotos(e.target.files)} data-testid="camera-input" />
            <Camera className="h-5 w-5" />
            <span className="text-[15px] font-medium">
              {photos.length ? "Scatta un'altra pagina" : "Scatta una foto del documento"}
            </span>
          </label>

          {photos.length > 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3" data-testid="photo-pages">
              <div className="flex items-center justify-between mb-2.5 px-1">
                <span className="label-caps">{photos.length === 1 ? "1 pagina da unire" : `${photos.length} pagine da unire`}</span>
                <button onClick={() => setPhotos([])} data-testid="clear-photos-btn"
                  className="text-xs text-slate-400 hover:text-white transition-colors">Rimuovi tutte</button>
              </div>
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                {photos.map((p, i) => (
                  <div key={i} className="relative shrink-0" data-testid={`photo-page-${i}`}>
                    <img src={URL.createObjectURL(p)} alt={`Pagina ${i + 1}`}
                      className="h-24 w-[4.5rem] object-cover rounded-lg ring-1 ring-white/15" />
                    <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/70 text-white rounded px-1.5 py-0.5">{i + 1}</span>
                    <button onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} aria-label="Rimuovi pagina"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#0b1226] border border-white/20 text-slate-300 hover:text-white hover:bg-red-600 transition-colors flex items-center justify-center">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2.5 px-1">
                Le pagine vengono unite in un unico PDF, nell'ordine mostrato, e l'AI legge il testo di ognuna.
              </p>
              <Button variant="outline" onClick={enhanceScan} disabled={scanning || analyzing}
                data-testid="scan-enhance-btn" className="w-full mt-3 h-11">
                {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanLine className="h-4 w-4 mr-2" />}
                {scanning ? "Sto raddrizzando le pagine..." : scanned ? "Rifai la pulizia scanner" : "Migliora come scanner"}
              </Button>
              {scanned && (
                <p className="text-xs text-emerald-300/90 mt-2 px-1 flex items-center gap-1.5" data-testid="scan-done-note">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Pagine ritagliate, raddrizzate e rese bianco e nero come una fotocopia.
                </p>
              )}
            </div>
          )}
          {file && file.type?.startsWith("image/") && (
            <p className="text-xs text-slate-500 -mt-2">
              Foto pronta: l'AI leggerà il testo dall'immagine (serve una foto nitida e ben illuminata).
            </p>
          )}

          <Button onClick={analyze} disabled={analyzing || (!file && photos.length === 0) || !activeTenant}
            data-testid="analyze-btn" className="w-full sm:w-auto h-12 sm:h-10 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {analyzing
              ? "Analisi AI in corso..."
              : photos.length > 1 ? `Unisci ${photos.length} pagine e analizza` : "Analizza con AI"}
          </Button>
        </Card>
      )}

      {step === "form" && (
        <div className="space-y-6">
          {editId && impact && (impact.incoming.length > 0 || impact.outgoing.length > 0) && (
            <Card className="p-5 border-amber-200 bg-amber-50/60" data-testid="impact-panel">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">Impatto delle modifiche</span>
              </div>
              <p className="text-xs text-amber-800/80 mb-3">
                Modificando questo documento potresti dover rivedere i documenti collegati qui sotto.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="label-caps mb-1.5 flex items-center gap-1"><GitBranch className="h-3 w-3" /> Dipendono da questo ({impact.incoming.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {impact.incoming.length ? impact.incoming.map((d) => (
                      <Badge key={d.id} variant="outline" className="bg-white font-mono text-[11px]" title={d.titolo}>{d.codice}</Badge>
                    )) : <span className="text-xs text-slate-500">Nessuno</span>}
                  </div>
                </div>
                <div>
                  <div className="label-caps mb-1.5 flex items-center gap-1"><GitBranch className="h-3 w-3" /> Questo dipende da ({impact.outgoing.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {impact.outgoing.length ? impact.outgoing.map((d) => (
                      <Badge key={d.id} variant="outline" className="bg-white font-mono text-[11px]" title={d.titolo}>{d.codice}</Badge>
                    )) : <span className="text-xs text-slate-500">Nessuno</span>}
                  </div>
                </div>
              </div>
            </Card>
          )}
          {raw && (
            <Card className="p-5 border-blue-200 bg-blue-50/50">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="h-4 w-4 text-blue-700" />
                <span className="text-sm font-medium text-blue-900">Estratto dall'intestazione (riferimento)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <RawItem label="Scopo" value={raw.scopo} />
                <RawItem label="Campo di applicazione" value={raw.campo_applicazione} />
                <RawItem label="Destinatario" value={raw.destinatario} />
                <RawItem label="Norma" value={raw.norma} />
                <RawItem label="Punti norma" value={(raw.punti_norma || []).join(", ")} />
              </div>
              <p className="text-xs text-blue-700/70 mt-3">Puoi correggere ogni campo qui sotto: i suggerimenti non sono vincolanti.</p>
            </Card>
          )}

          <Card className="p-6 border-slate-200 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Codice documento *</Label><Input value={form.codice} data-testid="doc-codice" onChange={(e) => setForm({ ...form, codice: e.target.value })} placeholder="PG-07" className="mt-1.5" /></div>
              <div className="md:col-span-2"><Label>Titolo *</Label><Input value={form.titolo} data-testid="doc-titolo" onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Descrizione (Scopo + Campo di applicazione)</Label>
              <Textarea value={form.descrizione} data-testid="doc-descrizione" onChange={(e) => setForm({ ...form, descrizione: e.target.value })} rows={4} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="doc-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_DOCUMENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Versione</Label><Input value={form.versione} onChange={(e) => setForm({ ...form, versione: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Emissione</Label><Input type="date" value={form.data_emissione} onChange={(e) => setForm({ ...form, data_emissione: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Prossima revisione</Label><Input type="date" value={form.data_prossima_revisione} onChange={(e) => setForm({ ...form, data_prossima_revisione: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Stato</Label>
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="doc-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI_DOCUMENTO.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Schemi di riferimento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="doc-schemes" className="mt-1.5 w-full flex items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-left hover:border-blue-400 transition-colors">
                      <span className="truncate">{form.scheme_ids?.length ? schemes.filter((s) => form.scheme_ids.includes(s.id)).map((s) => s.sigla).join(", ") : "Nessuno"}</span>
                      <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(20rem,calc(100vw-2rem))] p-2" align="start">
                    <p className="text-[11px] text-slate-500 px-1 pb-1.5">Seleziona uno o più schemi (sistema di gestione integrato)</p>
                    {schemes.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer" data-testid={`doc-scheme-${s.id}`}>
                        <Checkbox checked={form.scheme_ids?.includes(s.id)} onCheckedChange={() => toggle("scheme_ids", s.id)} />
                        <span className="text-sm">{s.sigla}</span>
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MultiCard title="Processi" testid="processes-picker" items={processes.map((p) => ({ id: p.id, label: p.nome }))} selected={form.process_ids} onToggle={(id) => toggle("process_ids", id)} empty="Nessun processo per questo cliente" />
            {editId ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="requirements-edit-note">
                <p className="text-sm font-medium mb-1">Requisiti coperti</p>
                <p className="text-xs text-slate-500">Le coperture (anche di sistemi integrati e quelle aggiunte a mano) si gestiscono nella pagina di dettaglio del documento e non vengono modificate salvando qui.</p>
              </div>
            ) : (
              <MultiCard title="Requisiti coperti" testid="requirements-picker" items={requirements.map((r) => ({ id: r.id, label: `${r.sigla ? r.sigla + " · " : ""}${r.codice} — ${r.titolo}` }))} selected={form.requirement_ids} onToggle={(id) => toggle("requirement_ids", id)} empty="Seleziona uno o più schemi" />
            )}
            <MultiCard title="Documenti collegati" testid="linked-picker" items={docs.filter((d) => d.id !== editId).map((d) => ({ id: d.id, label: `${d.codice} — ${d.titolo}` }))} selected={form.linked_document_ids} onToggle={(id) => toggle("linked_document_ids", id)} empty="Nessun altro documento" />
          </div>

          <div className="flex justify-end gap-2">
            {!editId && <Button variant="outline" onClick={() => setStep("upload")}>Indietro</Button>}
            <Button onClick={save} disabled={saving} data-testid="save-document-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Salva documento
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepDot({ active, done, n, label }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "text-blue-800" : "text-slate-500"}`}>
      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium ${active ? "bg-blue-700 text-white" : done ? "bg-blue-100 text-blue-700" : "bg-slate-100"}`}>{n}</span>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function RawItem({ label, value }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-blue-700/70">{label}: </span>
      <span className="text-slate-700">{value || "—"}</span>
    </div>
  );
}

function MultiCard({ title, items, selected, onToggle, empty, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-medium">{title}</h3>
        {selected.length > 0 && <Badge className="bg-blue-100 text-blue-800 border-blue-200">{selected.length}</Badge>}
      </div>
      <div className="p-3 max-h-64 overflow-y-auto space-y-1">
        {items.length === 0 ? <p className="text-xs text-slate-500 p-2">{empty}</p> : items.map((it) => (
          <label key={it.id} className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => onToggle(it.id)} data-testid={`pick-${it.id}`} className="mt-0.5" />
            <span>{it.label}</span>
          </label>
        ))}
      </div>
    </Card>
  );
}
